import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Megaphone, Check, ChevronDown, ChevronUp } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const MAX_COLLAPSED = 2;

const AnnouncementsBanner = () => {
  const [items, setItems] = useState([]);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    axios.get(`${API}/announcements`, { withCredentials: true }).then(({ data }) => setItems(data)).catch(() => {});
  }, []);

  const ack = async (id) => {
    try {
      await axios.post(`${API}/announcements/${id}/ack`, {}, { withCredentials: true });
      toast.success('Acknowledged');
      setItems(items.map(i => i.id === id ? { ...i, acknowledged: true } : i));
    } catch { toast.error('Failed'); }
  };

  // Only show un-acked OR recent (last 7 days), but never if already acked
  const visible = items.filter(i => {
    if (i.acknowledged) return false;
    const d = new Date(i.created_at).getTime();
    return Date.now() - d < 30 * 24 * 3600 * 1000;
  });

  if (visible.length === 0) return null;

  const shown = expanded ? visible : visible.slice(0, MAX_COLLAPSED);
  const hidden = visible.length - shown.length;

  return (
    <div data-testid="announcements-banner" className="space-y-2 mb-4">
      {shown.map((a) => (
        <div key={a.id} className="bg-gradient-to-r from-[#1a1505] via-[#141414] to-[#141414] border border-[#D4AF37]/50 rounded-md p-3 flex items-start gap-3">
          <div className="w-7 h-7 bg-[#D4AF37]/10 rounded-md flex items-center justify-center flex-shrink-0">
            <Megaphone className="w-3.5 h-3.5 text-[#D4AF37]" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between gap-3">
              <p className="text-white font-bold text-sm truncate">{a.title}</p>
              <p className="text-[#525252] text-[10px] flex-shrink-0">{new Date(a.created_at).toLocaleDateString()}</p>
            </div>
            {a.body && <p className="text-[#A3A3A3] text-xs mt-0.5 line-clamp-2">{a.body}</p>}
            {a.requires_ack && !a.acknowledged && (
              <button
                data-testid={`announcement-ack-button-${a.id}`}
                onClick={() => ack(a.id)}
                className="mt-2 inline-flex items-center gap-1.5 bg-[#D4AF37] text-[#0A0A0A] px-2.5 py-1 rounded-md text-[11px] font-bold hover:bg-[#F3E5AB]"
              >
                <Check className="w-3 h-3" /> Acknowledge
              </button>
            )}
          </div>
        </div>
      ))}
      {hidden > 0 && !expanded && (
        <button
          data-testid="announcements-expand"
          onClick={() => setExpanded(true)}
          className="w-full text-center text-[#D4AF37] text-xs hover:text-[#F3E5AB] py-1 flex items-center justify-center gap-1"
        >
          <ChevronDown className="w-3 h-3" /> Show {hidden} more
        </button>
      )}
      {expanded && visible.length > MAX_COLLAPSED && (
        <button
          data-testid="announcements-collapse"
          onClick={() => setExpanded(false)}
          className="w-full text-center text-[#A3A3A3] text-xs hover:text-white py-1 flex items-center justify-center gap-1"
        >
          <ChevronUp className="w-3 h-3" /> Collapse
        </button>
      )}
    </div>
  );
};

export default AnnouncementsBanner;
